import { Settings, Activity, Disc3, Wrench, Fuel, Gauge, Snowflake } from 'lucide-react';

// Business original images
const IMAGES = {
  transmission: "/images/IMG_1681.jpg",
  diagnostic: "/images/IMG_1658.jpg",
  brakes: "/images/brakes_service.jpg",
  ac: "/images/ac_service.jpg",
  tuneup: "/images/IMG_1746.jpg",
  diesel: "/images/IMG_1788.jpg",
  gasoline: "/images/IMG_1720.jpg",
};

const services = [
  {
    id: 1,
    name: "Transmission Rebuild",
    nameEs: "Reconstrucción de Transmisión",
    description: "Servicios completos de reconstrucción y reparación de transmisiones automáticas y manuales.",
    icon: Settings,
    image: IMAGES.transmission,
    large: true,
  },
  {
    id: 2,
    name: "Engine Diagnostic",
    nameEs: "Diagnóstico de Motor",
    description: "Diagnósticos computarizados avanzados para identificar y solucionar problemas del motor con precisión.",
    icon: Activity,
    image: IMAGES.diagnostic,
    large: true,
  },
  {
    id: 3,
    name: "Air Conditioning",
    nameEs: "Aire Acondicionado",
    description: "Servicio completo de A/C: diagnóstico, recarga de refrigerante, reparación de compresor y mantenimiento del sistema de climatización.",
    icon: Snowflake,
    image: IMAGES.ac,
    large: true,
  },
  {
    id: 4,
    name: "Brakes",
    nameEs: "Frenos",
    description: "Reparación completa del sistema de frenos incluyendo pastillas, rotores, calibradores y servicio de líquido de frenos.",
    icon: Disc3,
    image: IMAGES.brakes,
    large: false,
  },
  {
    id: 5,
    name: "Tune Up",
    nameEs: "Afinación",
    description: "Servicios integrales de afinación para optimizar el rendimiento y eficiencia de su vehículo.",
    icon: Wrench,
    image: IMAGES.tuneup,
    large: false,
  },
  {
    id: 6,
    name: "Diesel Engine",
    nameEs: "Motor Diesel",
    description: "Reparación especializada de motores diesel, mantenimiento y optimización de rendimiento.",
    icon: Fuel,
    image: IMAGES.diesel,
    large: false,
  },
  {
    id: 7,
    name: "Gasoline Engine",
    nameEs: "Motor a Gasolina",
    description: "Reparación experta de motores a gasolina, mantenimiento y servicios de overhaul completo.",
    icon: Gauge,
    image: IMAGES.gasoline,
    large: false,
  },
];

const ServicesSection = () => {
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      id="services" 
      className="py-20 md:py-32 bg-zinc-950"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <div className="mb-16">
          <span 
            className="text-xs font-bold uppercase tracking-[0.2em] text-yellow-400 mb-4 block"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            Lo Que Hacemos
          </span>
          <h2 
            className="text-4xl sm:text-5xl font-bold uppercase tracking-tight text-white"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            data-testid="services-title"
          >
            Nuestros Servicios
          </h2>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          {services.map((service, index) => {
            const Icon = service.icon;
            const isLarge = service.large;
            
            return (
              <div
                key={service.id}
                className={`
                  group relative overflow-hidden bg-zinc-900 border border-zinc-800 
                  hover:border-blue-600 transition-all duration-300 cursor-pointer
                  ${isLarge ? 'md:col-span-6' : 'md:col-span-4'}
                  ${isLarge ? 'min-h-[400px]' : 'min-h-[280px]'}
                `}
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`service-card-${service.id}`}
                onClick={() => scrollToSection('#appointments')}
              >
                {/* Background Image for large cards */}
                {service.image && (
                  <div 
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                    style={{ backgroundImage: `url(${service.image})` }}
                  />
                )}
                
                {/* Overlay */}
                <div className={`absolute inset-0 ${service.image ? 'bg-gradient-to-t from-zinc-950 via-zinc-950/80 to-transparent' : ''}`} />
                
                {/* Content */}
                <div className="relative h-full p-8 flex flex-col justify-end">
                  {/* Icon */}
                  <div className="mb-4">
                    <div className="w-14 h-14 bg-blue-600/20 border border-blue-600/40 flex items-center justify-center group-hover:bg-blue-600 transition-colors">
                      <Icon className="text-yellow-400 group-hover:text-white transition-colors" size={28} />
                    </div>
                  </div>
                  
                  {/* Title */}
                  <h3 
                    className="text-2xl font-bold uppercase tracking-tight text-white mb-2"
                    style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                  >
                    {service.nameEs}
                  </h3>
                  
                  {/* English name */}
                  <p className="text-yellow-400 text-sm uppercase tracking-wider mb-3">
                    {service.name}
                  </p>
                  
                  {/* Description */}
                  <p className="text-zinc-400 text-sm leading-relaxed">
                    {service.description}
                  </p>
                </div>
                
                {/* Hover accent line */}
                <div className="absolute bottom-0 left-0 w-0 h-1 bg-blue-600 group-hover:w-full transition-all duration-500" />
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 text-center">
          <p className="text-zinc-400 mb-6">
            Especialistas en vehículos <span className="text-white font-semibold">domésticos e importados</span>
          </p>
          <button
            onClick={() => scrollToSection('#appointments')}
            className="bg-blue-600 text-white font-bold uppercase tracking-widest px-8 py-4 hover:bg-blue-700 transition-all"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            data-testid="services-cta"
          >
            Solicitar Servicio
          </button>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
